import React, { useState, useRef, useEffect } from "react";
import ApiService from "../../services/Api.service";
import "./ManageReportHeader.css";
import { ArrowLeft, MinusCircle, PlusCircle, RefreshCcw } from "lucide-react";
import Alert from "../../components/Alert/Alert";
import Button from "../../components/Button/Button";

export default function ManageReportHeader({ selectedTemplate, handleBack, hideHeader = false }) {
  const [defaultHeaders, setDefaultHeaders] = useState([]);
  const [usedHeaders, setUsedHeaders] = useState([]);
  const [availableHeaders, setAvailableHeaders] = useState([]);
  const [initialUsedHeaders, setInitialUsedHeaders] = useState([]);
  const [weight, setWeight] = useState(false); // default = false (No)


  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const dragType = useRef(null);

  const [alertValue, setAlertValue] = useState({
    show: false,
    message: "",
    variant: "success",
    position: "middle",
  });

  const normalizeHeaderList = (list, allHeaders) => {
    if (!Array.isArray(list)) return [];
    const headerMap = {};
    (allHeaders || []).forEach((h) => {
      if (h && h.value) headerMap[h.value] = h.label;
    });

    return list.map((item) => {
      if (typeof item === "object" && item !== null && item.value) {
        return {
          value: item.value,
          label: item.label || headerMap[item.value] || item.value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        };
      }
      const val = String(item);
      return {
        value: val,
        label: headerMap[val] || val.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      };
    });
  };

  // Helper to update both used and available based on usedHeaders array
  const updateUsedAndAvailable = (used, allHeaders = defaultHeaders) => {
    const normalizedUsed = normalizeHeaderList(used, allHeaders);
    setUsedHeaders(normalizedUsed);
    const usedSet = new Set(normalizedUsed.map((h) => h.value));
    const available = (allHeaders || []).filter((h) => !usedSet.has(h.value));
    setAvailableHeaders(available);
  };

  // Load default headers on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await ApiService.getDefaultReportHeaders();
        if (res.status === 200) {
          const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
          setDefaultHeaders(list);
        }
      } catch (err) {
        console.error("Failed to load default report headers", err);
      }
    })();
  }, []);

  // Load used headers on selectedTemplate change, then compute available headers
  useEffect(() => {
    const tplId = selectedTemplate?.id || selectedTemplate?._id;
    if (defaultHeaders.length === 0) return;

    if (!tplId) {
      updateUsedAndAvailable([], defaultHeaders);
      return;
    }

    async function fetchHeaders() {
      try {
        const res = await ApiService.getReportFormat(tplId);
        if (res.status === 200 && res.data) {
          const rawUsed = res.data.default_template || [];
          const normalized = normalizeHeaderList(rawUsed, defaultHeaders);
          setInitialUsedHeaders(normalized);
          updateUsedAndAvailable(normalized, defaultHeaders);
          if (typeof res.data.weight === "boolean") {
            setWeight(res.data.weight);
          }
        }
      } catch (err) {
        console.error("Error loading report headers", err);
      }
    }
    fetchHeaders();
  }, [selectedTemplate, defaultHeaders]);

  // Reset to last saved state from server (initialUsedHeaders)
  const resetHeaders = () => {
    updateUsedAndAvailable(initialUsedHeaders);
  };

  // Save current usedHeaders to backend API
  const saveAsCustom = async () => {
    const tplId = selectedTemplate?.id || selectedTemplate?._id;
    if (!tplId) {
      setAlertValue({
        show: true,
        message: "Please save template first before configuring report headers.",
        variant: "error",
        position: "middle",
      });
      return;
    }

    try {
      const default_template = usedHeaders.map((h) => h.value);
      const payload = { default_template, weight };

      const res = await ApiService.updateReportFormat(tplId, payload);

      if (res.status === 200) {
        setInitialUsedHeaders([...usedHeaders]);
        setAlertValue({
          show: true,
          message: res.data?.message || "Report format saved successfully",
          variant: "success",
          position: "middle",
        });
        if (handleBack) handleBack();
      } else {
        setAlertValue({
          show: true,
          message: "Failed to save report format",
          variant: "error",
          position: "middle",
        });
      }
    } catch (error) {
      console.error("Error saving report format:", error);
      setAlertValue({
        show: true,
        message: error.response?.data?.detail || "Failed to save report format",
        variant: "error",
        position: "middle",
      });
    }
  };

  // Drag handlers
  const handleDragStart = (index, type) => {
    dragItem.current = index;
    dragType.current = type;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDrop = (targetType) => {
    const origin = dragType.current;

    if (origin === null || dragItem.current === null || dragOverItem.current === null) {
      dragItem.current = null;
      dragOverItem.current = null;
      dragType.current = null;
      return;
    }

    // Dragging inside same list: reorder
    if (origin === targetType) {
      if (origin === "used") {
        const list = [...usedHeaders];
        const moved = list[dragItem.current];
        list.splice(dragItem.current, 1);
        list.splice(dragOverItem.current, 0, moved);
        setUsedHeaders(list);
      } else if (origin === "available") {
        const list = [...availableHeaders];
        const moved = list[dragItem.current];
        list.splice(dragItem.current, 1);
        list.splice(dragOverItem.current, 0, moved);
        setAvailableHeaders(list);
      }
    }

    // Dragging from available to used
    else if (origin === "available" && targetType === "used") {
      const moved = availableHeaders[dragItem.current];

      // Remove from availableHeaders
      const newAvailable = [...availableHeaders];
      newAvailable.splice(dragItem.current, 1);

      // Insert into usedHeaders at dragOverItem position (or at end)
      const newUsed = [...usedHeaders];
      const insertIndex = dragOverItem.current !== null ? dragOverItem.current : newUsed.length;
      newUsed.splice(insertIndex, 0, moved);

      updateUsedAndAvailable(newUsed);
    }

    // Dragging from used to available
    else if (origin === "used" && targetType === "available") {
      const moved = usedHeaders[dragItem.current];

      // Remove from usedHeaders
      const newUsed = [...usedHeaders];
      newUsed.splice(dragItem.current, 1);

      // Insert into availableHeaders at dragOverItem position (or at end)
      const newAvailable = [...availableHeaders];
      const insertIndex = dragOverItem.current !== null ? dragOverItem.current : newAvailable.length;
      newAvailable.splice(insertIndex, 0, moved);

      updateUsedAndAvailable(newUsed);
    }

    dragItem.current = null;
    dragOverItem.current = null;
    dragType.current = null;
  };

  // Add header from Available to Used via + button
  const addHeader = (index) => {
    const headerToAdd = availableHeaders[index];
    if (!headerToAdd) return;

    const newUsed = [...usedHeaders, headerToAdd];
    updateUsedAndAvailable(newUsed);
  };

  // Remove header from Used to Available via - button
  const removeHeader = (index) => {
    const headerToRemove = usedHeaders[index];
    if (!headerToRemove) return;

    const newUsed = [...usedHeaders];
    newUsed.splice(index, 1);

    updateUsedAndAvailable(newUsed);
  };

  return (
    <>
      {alertValue.show && (
        <Alert
          variant={alertValue.variant}
          message={alertValue.message}
          dismissible={true}
          timeout={2500}
          position={alertValue.position}
          onClose={() => setAlertValue({ show: false, message: "", variant: "" })}
        />
      )}
      {!hideHeader && (
      <div className="template-header">
        <div style={{ display: "flex", gap: 10 ,marginBottom:"10px"}}>
          <Button variant="ghost" icon={<ArrowLeft size={18}/>} onClick={handleBack} />

          <h5 className="template-title">Edit Report ({selectedTemplate.name})</h5>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Button variant="outline" icon={<RefreshCcw size={16}/>} onClick={resetHeaders}>Reset</Button>

          <Button variant="primary" onClick={saveAsCustom}>Save Changes</Button>
        </div>
      </div>
      )}

      {hideHeader && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
            <label style={{ fontWeight: 600, margin: 0 }}>Weight</label>
            <label style={{ margin: 0 }}>
              <input
                type="radio"
                name="weight"
                checked={weight === true}
                onChange={() => setWeight(true)}
              />{" "}Yes
            </label>
            <label style={{ margin: 0 }}>
              <input
                type="radio"
                name="weight"
                checked={weight === false}
                onChange={() => setWeight(false)}
              />{" "}No
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="outline" icon={<RefreshCcw size={16}/>} onClick={resetHeaders}>Reset</Button>
            <Button variant="primary" onClick={saveAsCustom}>Save Changes</Button>
          </div>
        </div>
      )}

      <div className="report-header-manager">
        <div className="rhm-container">
          {/* Available Headers */}
          <div
            className="rhm-col"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop("available")}
          >
            <h3>Available Headers</h3>
            <div className="rhm-list">
              {availableHeaders.length === 0 ? (
                <div className="empty-placeholder">No available headers</div>
              ) : (
                availableHeaders.map((item, index) => (
                  <div
                    key={item.value}
                    draggable
                    className="rhm-item available"
                    onDragStart={() => handleDragStart(index, "available")}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={() => handleDrop("available")}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <span>{index + 1}. {item.label}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        addHeader(index);
                      }}
                      className="add-to-use-btn"
                      title="Add to Used"
                    >
                      <PlusCircle />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="rhm-dropzone">Drop here to add</div>
          </div>

          {/* Used Headers */}
          <div
            className="rhm-col"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop("used")}
          >
            <h3>Used Headers</h3>
            <div className="rhm-list">
              {usedHeaders.length === 0 ? (
                <div className="empty-placeholder">No used headers</div>
              ) : (
                usedHeaders.map((item, index) => (
                  <div
                    key={item.value}
                    draggable
                    className="rhm-item used"
                    onDragStart={() => handleDragStart(index, "used")}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={() => handleDrop("used")}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    <span>{index + 1}. {item.label}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeHeader(index);
                      }}
                      className="remove-btn"
                      title="Remove to Available"
                    >
                      <MinusCircle />
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="rhm-dropzone">Drop here to remove</div>
          </div>
        </div>
      </div>
    </>
  );
}
